import {
	createSlice,
	createAsyncThunk,
	createSelector,
} from "@reduxjs/toolkit";
import instance from "../apiServices/instance";

// Async thunks for API calls
export const fetchRootFiles = createAsyncThunk(
	"documents/fetchRootFiles",
	async (_, { rejectWithValue }) => {
		try {
			const response = await instance.get("/api/my-files?folderPath=");
			return response.data || [];
		} catch (error) {
			return rejectWithValue(error.message);
		}
	}
);

export const fetchFolders = createAsyncThunk(
	"documents/fetchFolders",
	async (_, { rejectWithValue }) => {
		try {
			const response = await instance.get(
				"/api/list-my-folders?parentFolder=Folders"
			);
			return response.data || [];
		} catch (error) {
			return rejectWithValue(error.message);
		}
	}
);

export const fetchFolderContents = createAsyncThunk(
	"documents/fetchFolderContents",
	async (folderPath, { rejectWithValue }) => {
		try {
			const response = await instance.get(
				`/api/my-files?folderPath=${folderPath}`
			);
			return response.data || [];
		} catch (error) {
			return rejectWithValue(error.message);
		}
	}
);

export const uploadFile = createAsyncThunk(
	"documents/uploadFile",
	async (formData, { rejectWithValue }) => {
		try {
			await instance.post("/api/upload-file", formData, {
				headers: {
					"Content-Type": "multipart/form-data",
				},
			});
			return true;
		} catch (error) {
			return rejectWithValue(error.message);
		}
	}
);

export const uploadSensitiveFileApprovalDocument = createAsyncThunk(
	"documents/uploadFile",
	async (formData, { rejectWithValue }) => {
		try {
			await instance.post(
				"/api/company-house/upload-sensitive-files",
				formData,
				{
					headers: {
						"Content-Type": "multipart/form-data",
					},
				}
			);
			return true;
		} catch (error) {
			return rejectWithValue(error.message);
		}
	}
);

export const createFolder = createAsyncThunk(
	"documents/createFolder",
	async (formData, { rejectWithValue }) => {
		try {
			await instance.post("/api/upload-file", formData, {
				headers: {
					"Content-Type": "multipart/form-data",
				},
			});
			return true;
		} catch (error) {
			return rejectWithValue(error.message);
		}
	}
);

export const deleteFiles = createAsyncThunk(
	"documents/deleteFiles",
	async (fileIds, { rejectWithValue }) => {
		try {
			const response = await instance.post("/api/delete-files", {
				fileIds: Array.isArray(fileIds) ? fileIds : [fileIds],
			});
			return response.data.results.successful || [];
		} catch (error) {
			return rejectWithValue(error.message);
		}
	}
);

export const downloadFiles = createAsyncThunk(
	"documents/downloadFiles",
	async (fileIds, { rejectWithValue }) => {
		try {
			const ids = Array.isArray(fileIds) ? fileIds : [fileIds];

			const response = await instance.post(
				"/api/user-files/download-files",
				{
					ids,
				}
			);

			if (response.status !== 200 || !response.data) {
				return rejectWithValue("Unexpected response");
			}

			const { files } = response.data;
			if (!files || !Array.isArray(files)) {
				return rejectWithValue("Invalid download files response");
			}

			return files;
		} catch (error) {
			return rejectWithValue(error.message);
		}
	}
);

const initialState = {
	rootFiles: [],
	folders: [],
	folderContents: [],
	selectedFolder: null,
	searchQuery: "",
	loading: false,
	error: null,
};

const documentsSlice = createSlice({
	name: "documents",
	initialState,
	reducers: {
		setSelectedFolder: (state, action) => {
			state.selectedFolder = action.payload;
		},
		clearSelectedFolder: (state) => {
			state.selectedFolder = null;
		},
		setSearchQuery: (state, action) => {
			state.searchQuery = action.payload;
		},
		clearSearchQuery: (state) => {
			state.searchQuery = "";
		},
	},
	extraReducers: (builder) => {
		builder
			// fetchRootFiles
			.addCase(fetchRootFiles.pending, (state) => {
				state.loading = true;
			})
			.addCase(fetchRootFiles.fulfilled, (state, action) => {
				state.rootFiles = action.payload;
				state.loading = false;
				state.error = null;
			})
			.addCase(fetchRootFiles.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload;
			})

			// fetchFolders
			.addCase(fetchFolders.pending, (state) => {
				state.loading = true;
			})
			.addCase(fetchFolders.fulfilled, (state, action) => {
				state.folders = action.payload;
				state.loading = false;
				state.error = null;
			})
			.addCase(fetchFolders.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload;
			})

			// fetchFolderContents
			.addCase(fetchFolderContents.pending, (state) => {
				state.loading = true;
			})
			.addCase(fetchFolderContents.fulfilled, (state, action) => {
				state.folderContents = action.payload;
				state.loading = false;
				state.error = null;
			})
			.addCase(fetchFolderContents.rejected, (state, action) => {
				state.loading = false;
				state.error = action.payload;
			})

			// deleteFiles
			.addCase(deleteFiles.fulfilled, (state, action) => {
				const deletedIds = new Set(action.payload);

				// Remove deleted files from rootFiles
				state.rootFiles = state.rootFiles.filter(
					(file) => !deletedIds.has(file.id)
				);

				// Remove deleted files from folderContents
				state.folderContents = state.folderContents.filter(
					(file) => !deletedIds.has(file.id)
				);
			});
	},
});

// Selectors with filtering based on search query
export const selectFilteredRootFiles = createSelector(
	[
		(state) => state.documents.rootFiles,
		(state) => state.documents.searchQuery,
	],
	(rootFiles, searchQuery) => {
		if (!searchQuery.trim()) return rootFiles;

		const query = searchQuery.toLowerCase().trim();
		return rootFiles.filter((file) => {
			const fileName = file.name?.toLowerCase() || "";
			const fileType = file.ext?.toLowerCase() || "";
			return fileName.includes(query) || fileType.includes(query);
		});
	}
);

export const selectFilteredFolders = createSelector(
	[
		(state) => state.documents.folders,
		(state) => state.documents.searchQuery,
	],
	(folders, searchQuery) => {
		if (!searchQuery.trim()) return folders;

		const query = searchQuery.toLowerCase().trim();
		return folders.filter((folder) => {
			const folderName = folder.name?.toLowerCase() || "";
			const folderPath = folder.path?.toLowerCase() || "";
			return folderName.includes(query) || folderPath.includes(query);
		});
	}
);

export const selectFilteredFolderContents = createSelector(
	[
		(state) => state.documents.folderContents,
		(state) => state.documents.searchQuery,
	],
	(folderContents, searchQuery) => {
		if (!searchQuery.trim()) return folderContents;

		const query = searchQuery.toLowerCase().trim();
		return folderContents.filter((file) => {
			const fileName = file.name?.toLowerCase() || "";
			const fileType = file.ext?.toLowerCase() || "";
			return fileName.includes(query) || fileType.includes(query);
		});
	}
);

export const {
	setSelectedFolder,
	clearSelectedFolder,
	setSearchQuery,
	clearSearchQuery,
} = documentsSlice.actions;

export default documentsSlice;
